import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { getFreemiumIntentFlow } from '@shared';

import { analytics } from '@/analytics';

import { useFreemiumCheckoutStore } from '../stores/freemium-checkout-store';
import { useFreemiumSelection } from './use-freemium-selection';
import { useFreemiumUrlState } from './use-freemium-url-state';

function safeTrack(eventName: string, properties: Record<string, unknown>) {
  try {
    analytics.track(eventName, properties, 'action');
  } catch {
    // Metrics must never break the paywall flow.
  }
}

export function useFreemiumGateFlow(clientId: string) {
  const navigate = useNavigate();
  const url = useFreemiumUrlState();
  const clientSecret = useFreemiumCheckoutStore(state => state.clientSecret);
  const selection = useFreemiumSelection(clientId);

  const hitTracked = useRef(false);
  useEffect(() => {
    if (!hitTracked.current) {
      hitTracked.current = true;
      safeTrack('freemium_paywall_hit', { clientId });
    }
  }, [clientId]);

  const recreateRequested = useRef(false);

  useEffect(() => {
    if (
      url.step === 'checkout' &&
      url.selectedTier &&
      !clientSecret &&
      !selection.isPending &&
      !recreateRequested.current
    ) {
      recreateRequested.current = true;
      selection.recreateCheckout(url.selectedTier);
    }
  }, [url.step, url.selectedTier, clientSecret, selection]);

  function continueFromIntent() {
    if (!url.intent) return;

    safeTrack('freemium_intent_selected', { clientId, intent: url.intent });

    const flow = getFreemiumIntentFlow(url.intent);

    if (flow.kind === 'reflexive') {
      url.goToReflexive();
    } else if (flow.kind === 'consultHandoff') {
      url.goToConsult();
    } else if (flow.kind === 'guidedPlanningDeferred') {
      navigate(`/clients/${clientId}/onboarding/guided-planning`);
    } else {
      url.goToPaywall(null);
    }
  }

  function selectReflexive(answer: string) {
    url.goToPaywall(answer);
  }

  function goToGuidedPlanning() {
    navigate(`/clients/${clientId}/onboarding/guided-planning/quotes-selection`);
  }

  return {
    ...url,
    clientSecret,
    continueFromIntent,
    selectReflexive,
    goToGuidedPlanning,
    ...selection,
  };
}
